import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

// PTO payout on the final check (CJ 2026-08-07, D246).
// Spec: docs/PTO-PAYOUT-ON-SEPARATION-SPEC.md
//
// Anyone whose last day falls on or before the end of the payroll week and who
// still has a balance gets a card here. Payroll will not run while an eligible
// card is unanswered — the whole point is that the question is asked while the
// final check can still carry the answer.
//
// Ineligible people are rendered as a read-only note rather than a choice.
// Offering a button the server will refuse invites someone to try to approve
// what the policy forbids, and then wonder why it failed.
//
// A top-level component, not one declared inside PayrollReportView's render:
// a component defined in a render body is a new type on every keystroke, so
// React remounts it and any focus is lost.
export function PtoPayoutCards({ weekStart, currentUser, onChanged }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await api('GET', `/api/admin/payroll/pto-payouts?week_start=${weekStart}`, null, headers);
      setRows(Array.isArray(r?.decisions) ? r.decisions : []);
    } catch (e) {
      // Don't blank the list on a refresh failure — a stale card is more use
      // than an empty panel, and the preflight still blocks the run.
      setErr(e?.message || String(e));
    }
  }, [weekStart, currentUser.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (weekStart) load(); }, [weekStart, load]);

  async function decide(row, decision) {
    if (decision === 'paid') {
      const ok = window.confirm(
        `Pay out ${row.hours} hours of PTO to ${row.name} — $${row.amount.toFixed(2)} on this check?\n\n` +
        `Their balance will be emptied. This is hard to undo once the check is cut.`
      );
      if (!ok) return;
    }
    setBusyId(row.user_id); setErr('');
    try {
      await api('POST', `/api/admin/payroll/pto-payouts/${row.user_id}`, {
        week_start: weekStart,
        decision,
        // The figure being authorised. The server refuses if the live balance
        // no longer matches, so an accrual landing between render and click
        // can't quietly enlarge a payment nobody approved.
        expected_hours: row.hours,
      }, headers);
      await load();
      if (onChanged) await onChanged();
    } catch (e) {
      let m = e?.message || 'Could not record that.';
      try { m = JSON.parse(m).error || m; } catch (_) {}
      setErr(m);
    } finally { setBusyId(null); }
  }

  if (!rows || rows.length === 0) return null;

  const undecided = rows.filter(r => r.decision == null && r.eligible).length;

  return (
    <div className="card" style={{ borderLeft: `4px solid ${undecided ? '#b48a47' : '#2e7d32'}` }}>
      <div className="text-base font-semibold mb-1">
        Final checks — PTO payout
        {undecided > 0 && <span style={{ color: '#8a5a1f' }}> · {undecided} to decide</span>}
      </div>
      <div style={{ fontSize: 12, color: '#757575', marginBottom: 8 }}>
        Staff whose last day falls in this pay period. Payout requires more than one year of
        service, counted across all stints.
      </div>

      {err && <div style={{ fontSize: 12, color: '#a8323a', background: '#faeceb', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}>{err}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r) => (
          <div key={r.user_id} style={{
            border: '1px solid #e6e6e6', borderRadius: 8, padding: '10px 12px',
            background: r.decision ? '#f7f7f7' : '#fff',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{r.name}</div>
              <div style={{ fontSize: 12, color: '#757575' }}>last day {r.last_day}</div>
            </div>
            <div style={{ fontSize: 12, color: '#37474f', marginTop: 2 }}>
              {r.tenure_label} of service · <b>{r.hours}h</b> accrued
              {r.hourly_rate > 0 && <> · ${r.hourly_rate.toFixed(2)}/hr → <b>${r.amount.toFixed(2)}</b></>}
            </div>

            {!r.eligible && (
              <div style={{ fontSize: 12, color: '#8a5a1f', marginTop: 6 }}>
                Under one year of service — no payout. Their balance stays as it is.
              </div>
            )}

            {r.decision === 'paid' && (
              <div style={{ fontSize: 12, color: '#2e5d42', marginTop: 6, fontWeight: 600 }}>
                ✓ Paid out — {r.decided_hours}h, ${Number(r.decided_amount).toFixed(2)}
                {r.confirmed_by_name ? ` · ${r.confirmed_by_name}` : ''}
              </div>
            )}
            {r.decision === 'declined' && (
              <div style={{ fontSize: 12, color: '#757575', marginTop: 6, fontWeight: 600 }}>
                Not paid out{r.confirmed_by_name ? ` · ${r.confirmed_by_name}` : ''} — balance left as it is.
              </div>
            )}

            {r.decision == null && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {r.eligible && (
                  <button onClick={() => decide(r, 'paid')} disabled={busyId === r.user_id}
                    style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 7,
                             padding: '6px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {busyId === r.user_id ? 'Saving…' : 'Pay it out'}
                  </button>
                )}
                <button onClick={() => decide(r, 'declined')} disabled={busyId === r.user_id}
                  style={{ background: '#fff', border: '1px solid #d0d4d9', borderRadius: 7,
                           padding: '6px 14px', fontSize: 12.5, fontWeight: 600, color: '#555', cursor: 'pointer', fontFamily: 'inherit' }}>
                  {r.eligible ? "Don't pay out" : 'Acknowledge'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
